import { categoryColour, formatCount, registerIds, tt, type CategoryColour } from '@agos/shared'

/**
 * `/sales-channels` — §9.5's channel administration, which has no legacy counterpart at all.
 *
 * §10A.1 gave the backend a `sales_channels` table and `SalesChannel`'s own docblock says how to use it:
 *
 * > Ask what a channel **does**, never what it is called. `channel.usesDrivers()` keeps working when a
 * > franchise adds a fourth channel; `"delivery".equals(type)` does not.
 *
 * Nothing in the frontend has been able to edit those capabilities, so a franchise adding a channel had to
 * do it in SQL. This screen is that editor — and it is a **justified EXTRA** in the §12.2 parity diff,
 * citing this section.
 *
 * **The seven flags are opaque names, and that is the actual problem.** `onPremise`, `usesTables` and
 * `allowsDeferredPayment` say nothing about what they do to an order, and each of them changes whether an
 * order can be closed. What each one decides is taken from the backend's own code, not guessed:
 *
 * | Flag | What it decides |
 * |---|---|
 * | `requiresDeliveryAddress` | whether an order counts as a delivery for the address book, the customer directory and the delay SMS |
 * | `usesDrivers` | `OrderStatusService`: moving an order to *on the way* requires a driver |
 * | `usesTables` | `requiresWaiter()` — only a waiter may close it — **and** `waitsForKitchen()` — it cannot be settled mid-service |
 * | `onPremise` | `requiresHandoverOwner()` — somebody must own the money before the order closes; also picks the VAT slot |
 * | `availableOnWeb` | whether a customer on the website may choose this channel |
 * | `allowsDeferredPayment` | whether orders may stay unpaid and accumulate on a tab (§10A's *na sekeru*) |
 * | `settlesImmediately` | the payment and the receipt open on save, and the order never reaches the dispatch board or *Otvorené stoly* - **§9.566 added this row, which said *seven flags* and listed six** |
| `offersAddons` | the till offers a product's own add-ons after adding it (§9.657) |
| `ownBoards` | the channel gets its own *Kuchyňa* and *Otvorené stoly* in the sidebar, and the ordinary two stop drawing its orders - §9.688, owner #24 (O79) |
| `unprintedCloseCancels` | on a channel that settles immediately only: *Zatvoriť bez fiškálneho dokladu* cancels the order (on, §9.457, the default) or closes it as paid (off) - §9.726, owner O96 |
 * | `offersToCouriers` | on a channel a driver takes only: *Rozvoz - nástenka* may offer its orders to external couriers (on, the default) or keeps them to your own drivers (off) - §9.792, owner O144 |
 *
 * **FB-181.** Two of the four live channels carry a flag their own orders contradict, and nothing anywhere
 * says so. `bar` has `usesDrivers: true`, and **137 of its 1 082 orders** have a driver — 12,7 %; the other
 * 945 were handed over the counter. `delivery` has `requiresDeliveryAddress: true` and all 7 436 of its
 * orders have one, which is consistent — but **122 of them have no driver** while `usesDrivers` is true.
 * A flag is a rule for the *next* order, so neither is a defect on its own; both are worth seeing before
 * somebody changes one, because the flag decides whether an order can be closed at all.
 */

export interface Channel {
  id: number
  name: string
  slug: string
  requiresDeliveryAddress: boolean
  usesDrivers: boolean
  usesTables: boolean
  onPremise: boolean
  availableOnWeb: boolean
  allowsDeferredPayment: boolean
  /** §10A.87 — paid and printed at the counter, so never on a board. */
  settlesImmediately: boolean
  sortOrder: number
  isActive: boolean
  /** §9.38. `null` for none, and for a value off the palette — `categoryColour`'s rule. */
  colour: CategoryColour | null
  /**
   * §10A.17 — the fiscal register this channel's receipts print on.
   *
   * `null` for *nobody has chosen*, in which case the server's capability rule decides. **Whatever the server
   * stored otherwise** - §9.650, FB-979.
   *
   * <p>This was narrowed to `KnownRegister`, so an id outside the two this build ships with read as `null`, on
   * the reasoning that *"the screen should offer what it can honour rather than show a value it cannot explain"*.
   * That holds for an off-palette colour, whose palette is twelve literals here, and breaks for a register: the
   * list comes from `/fiskal/registers`, so the screen **can** explain a third one, and `FiskalRegisters.create`
   * accepts any `^[a-z][a-z0-9_-]{1,31}$` slug. The owner created *Kasa Terasa*, the save stored it, and one
   * press of *Upraviť* read it back as *Podľa typu predaja*.
   */
  /** §10A.331, owner O44 - whether this channel's till offers a product's add-ons after adding it. */
  offersAddons: boolean
  /** §10A.347, owner #24 (O79) - whether this channel runs its own Kuchyňa and Otvorené stoly. */
  ownBoards: boolean
  /** §10A.374, owner O96 - on a settling channel, whether a close without a fiscal document cancels the order. */
  unprintedCloseCancels: boolean
  /** §10A.443, owner O144 - on a driven channel, whether its orders may be offered to external couriers. */
  offersToCouriers: boolean
  kasaId: string | null
  /**
   * §10A.388, §9.740, owner O110 - on a channel both on the premises and driven, the registers the person printing
   * chooses from at the till and on the board. Empty is *no list*.
   */
  printKasaIds: string[]
}

/**
 * §9.650 - `KNOWN_REGISTERS`, `KnownRegister`, `knownRegister` and `registerLabel` were here and are gone.
 *
 * <p>All four are already answered by `@agos/shared`: {@link registerIds} draws the list the endpoint returned
 * and {@link registerName} names an id from the rows, falling back to {@link SHIPPED_REGISTER_NAMES} and then to
 * the id itself. §9.578 extracted them for exactly this feature and this screen kept its own copies, which is
 * how the two came to disagree - `ZonesPanel` keeps whatever is stored and this threw it away.
 *
 * <p>The old comment read *"If a third is ever configured, this is the one place that has to learn it."* It was
 * right about where and it was a note rather than a mechanism.
 */

/** The same as {@link text}, with the whitespace off - §9.650. */
function trimmedText(value: unknown): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed === '' ? null : trimmed
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function channelsOf(payload: unknown): Channel[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      if (typeof row.id !== 'number') return []
      return [
        {
          id: row.id,
          name: text(row.name) ?? '',
          slug: text(row.slug) ?? '',
          requiresDeliveryAddress: row.requiresDeliveryAddress === true,
          usesDrivers: row.usesDrivers === true,
          usesTables: row.usesTables === true,
          onPremise: row.onPremise === true,
          availableOnWeb: row.availableOnWeb === true,
          allowsDeferredPayment: row.allowsDeferredPayment === true,
          settlesImmediately: row.settlesImmediately === true,
          // §10A.331 - `=== true`, so an older server that does not send it reads as off, which is the
          // column's own default and means no till's behaviour changes on a stale backend.
          offersAddons: row.offersAddons === true,
          // §10A.347 - `=== true` for the same reason: an older server reads as *no own boards*.
          ownBoards: row.ownBoards === true,
          // §10A.374 - **`!== false`, the other way round**: this column defaults to true (§9.457's cancellation),
          // so an older server reads as that, and the editor does not open on an *off* nobody chose.
          unprintedCloseCancels: row.unprintedCloseCancels !== false,
          // §10A.443 - `!== false`, the column's default true: an older server reads as today's couriers' button.
          offersToCouriers: row.offersToCouriers !== false,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
          isActive: row.isActive === true,
          colour: categoryColour(row.color),
          /* §9.650 - whatever the server stored. **Trimmed**, which `text()` does not do and the narrowing it
             replaced did: `FiskalRegisters.requireKnown` trims before comparing, so a padded id names a real
             register on the server and would be read here as a different one - and sent back padded. A case
             written for this failed on the first draft of this line. */
          kasaId: trimmedText(row.kasaId),
          // §10A.388 - an older server sends nothing, which is *no list*.
          printKasaIds: Array.isArray(row.printKasaIds)
            ? row.printKasaIds.flatMap((id) => {
                const trimmed = typeof id === 'string' ? id.trim() : ''
                return trimmed === '' ? [] : [trimmed]
              })
            : [],
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id - right.id)
}

// ─── The seven flags, and what each one does ─────────────────────────────────
//
// §10A.87 made it seven: `settlesImmediately` is the owner's *Do ruky*. Every other flag here says
// what an order **needs**; that one says when it is **finished**.

export type CapabilityKey =
  | 'requiresDeliveryAddress'
  | 'usesDrivers'
  /** §10A.443, owner O144 - on a driven channel, whether the board may offer its orders to external couriers. */
  | 'offersToCouriers'
  | 'usesTables'
  | 'onPremise'
  | 'availableOnWeb'
  | 'allowsDeferredPayment'
  | 'settlesImmediately'
  /** §10A.374, owner O96 - on a settling channel, whether a close without a document cancels the order. */
  | 'unprintedCloseCancels'
  /** §10A.331, owner O44 - whether the till offers the product's add-ons after adding it. */
  | 'offersAddons'
  /** §10A.347, owner #24 (O79) - its own Kuchyňa and Otvorené stoly. */
  | 'ownBoards'

export const CAPABILITIES: readonly CapabilityKey[] = [
  'requiresDeliveryAddress',
  'usesDrivers',
  // §9.792 - directly under the flag it depends on, as `unprintedCloseCancels` is under its own.
  'offersToCouriers',
  'usesTables',
  'onPremise',
  'availableOnWeb',
  'allowsDeferredPayment',
  'settlesImmediately',
  // §9.726 - directly under the flag it depends on, which is where the editor draws it (`capabilityApplies`).
  'unprintedCloseCancels',
  'offersAddons',
  'ownBoards',
]

/**
 * §9.566 - a `switch` with no `default`, so an eighth capability is **TS2366** rather than a checkbox.
 *
 * The chain this replaced ended in a bare `return tt('Zaplatí sa hneď')`, which is `settlesImmediately`'s
 * label. So a capability added to {@link CapabilityKey} and {@link CAPABILITIES} would have been drawn
 * telling the operator it settles the order at once - §9.502's class, on the screen whose whole difficulty
 * is that these names say nothing. §9.512's precedent: removing a `default` from `pointUrl` is what caught
 * Mapy.com instead of shipping a Google link under a Mapy label.
 */
export function capabilityLabel(key: CapabilityKey): string {
  switch (key) {
    case 'requiresDeliveryAddress':
      return tt('Vyžaduje adresu')
    case 'usesDrivers':
      return tt('Rozváža šofér')
    case 'offersToCouriers':
      return tt('Ponúka externým')
    case 'usesTables':
      return tt('Obsluhuje stoly')
    case 'onPremise':
      return tt('Na prevádzke')
    case 'availableOnWeb':
      return tt('Dostupné na webe')
    case 'allowsDeferredPayment':
      return tt('Platba neskôr')
    case 'settlesImmediately':
      return tt('Zaplatí sa hneď')
    case 'unprintedCloseCancels':
      return tt('Bez dokladu sa zruší')
    case 'offersAddons':
      return tt('Ponúkne doplnky')
    case 'ownBoards':
      return tt('Vlastná kuchyňa a stoly')
  }
}

/**
 * What the flag changes about an order, taken from the backend rather than from the name.
 *
 * <p>§9.566 - exhaustive for {@link capabilityLabel}'s reason, and **both matter**: a label with no meaning
 * is a checkbox nobody can act on, and a meaning with no label is a sentence about nothing.
 */
export function capabilityMeaning(key: CapabilityKey): string {
  switch (key) {
    case 'requiresDeliveryAddress':
      return tt('Objednávka sa počíta ako rozvoz - do adresára zákazníka, do prehľadu a do SMS o meškaní.')
    case 'usesDrivers':
      return tt('Bez prideleného šoféra sa objednávka nedá prepnúť na „na ceste“.')
    case 'offersToCouriers':
      return tt(
        'Len pre kanál, ktorý rozváža šofér. Zapnuté: na nástenke Rozvoz sa pri objednávke tohto kanála ukáže Ponúknuť externým. Vypnuté: objednávky tohto kanála rozvážajú len vaši šoféri a server ich externým neponúkne.',
      )
    case 'usesTables':
      return tt(
        'Uzavrieť ju smie len čašník a až keď kuchyňa dokončí - stôl sa nedá zúčtovať uprostred obsluhy.',
      )
    case 'onPremise':
      return tt(
        'Peniaze musí niekto prevziať - objednávka sa nedá uzavrieť bez majiteľa. Rozhoduje aj o sadzbe DPH.',
      )
    case 'availableOnWeb':
      return tt('Zákazník si tento kanál môže vybrať na webe. Bez neho ho vidí len obsluha.')
    case 'allowsDeferredPayment':
      return tt('Objednávky môžu ostať nezaplatené a načítavať sa na účet („na sekeru“).')
    case 'settlesImmediately':
      return tt(
        'Po uložení sa hneď otvorí platba a doklad - objednávka sa neobjaví na nástenke Rozvoz ani v Otvorených stoloch.',
      )
    case 'unprintedCloseCancels':
      return tt(
        'Len pre kanál, ktorý sa zaplatí hneď. Zapnuté: Zatvoriť bez fiškálneho dokladu objednávku zruší - nezostane v tržbe dňa a obsluha na to potrebuje právo Stornovať objednávku. Vypnuté: objednávka sa uzavrie ako zaplatená, len bez fiškálneho dokladu.',
      )
    case 'offersAddons':
      return tt(
        'Po pridaní produktu sa obsluhe ponúknu jeho doplnky (Doplnky k produktu) a môže ich pridať do objednávky.',
      )
    case 'ownBoards':
      return tt(
        'Kanál má v menu vlastnú Kuchyňu a vlastné Otvorené stoly - v hlavnej Kuchyni a Otvorených stoloch sa jeho objednávky nezobrazujú. Po vypnutí sa vrátia tam.',
      )
  }
}

export function capabilityOf(channel: Channel, key: CapabilityKey): boolean {
  return channel[key]
}

/**
 * Whether a capability means anything on a channel with these flags - §9.726, owner O96.
 *
 * <p>**Two capabilities depend on another**: `unprintedCloseCancels` decides what a close without a fiscal document
 * does on a channel that settles immediately, and nothing anywhere else - every other channel's such close has
 * always settled (`BACKEND.md` §10A.374). So its checkbox and its chip are drawn only where `settlesImmediately` is
 * on, and ticking *Zaplatí sa hneď* in the editor draws it right under that box. **§9.792 adds the second**:
 * `offersToCouriers` is about the board's *Ponúknuť externým*, which only a driven order has, so it is drawn only
 * where `usesDrivers` is on. Every other capability applies everywhere. Takes the form or the channel, which share
 * the fields it reads.
 */
export function capabilityApplies(
  key: CapabilityKey,
  flags: { settlesImmediately: boolean; usesDrivers: boolean },
): boolean {
  if (key === 'unprintedCloseCancels') return flags.settlesImmediately
  if (key === 'offersToCouriers') return flags.usesDrivers
  return true
}

// ─── FB-181: what the orders actually did ────────────────────────────────────

/**
 * How many orders a channel carries.
 *
 * **Two endpoints answer this in two shapes, and they are not interchangeable.** `/analytics/revenue` sends
 * `channelSplit` keyed by **`type`**; `/dashboard/stats` sends `revenueByChannel` keyed by **`slug`**. They
 * hold the same value — `orders.type` stores the channel's slug — under different names, so both are read
 * rather than one being assumed. Getting this wrong is silent: the block simply is not there and every
 * channel reports *no orders*, which is exactly what a genuinely unused channel reports.
 *
 * There is no endpoint that answers *how many bar orders actually had a driver*, so FB-181's other half is
 * filed as a backend item rather than derived from something that does not mean it.
 */
export interface ChannelTrade {
  slug: string
  orders: number
}

export function tradeOf(payload: unknown): ChannelTrade[] {
  const source = (payload ?? {}) as Record<string, unknown>
  const rows = Array.isArray(source.channelSplit)
    ? source.channelSplit
    : Array.isArray(source.revenueByChannel)
      ? source.revenueByChannel
      : []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    // `slug` on the dashboard's block, `type` on the analytics one. Same value.
    const slug = text(row.slug) ?? text(row.type)
    if (slug === null) return []
    return [{ slug, orders: typeof row.orders === 'number' ? row.orders : 0 }]
  })
}

export function ordersFor(slug: string, trade: readonly ChannelTrade[]): number | null {
  const match = trade.find((row) => row.slug === slug)
  return match === undefined ? null : match.orders
}

/**
 * Whether switching this channel off, or deleting it, would strand trade.
 *
 * `DELETE /sales-channels/{id}` is documented *"Delete a sales channel that has no orders"*, so the server
 * refuses one that does. Saying so before the click is better than a red toast after it.
 */
export function deleteBlocked(channel: Channel, trade: readonly ChannelTrade[]): boolean {
  return (ordersFor(channel.slug, trade) ?? 0) > 0
}

export function deleteWarning(channel: Channel, trade: readonly ChannelTrade[]): string {
  const orders = ordersFor(channel.slug, trade)
  if (orders !== null && orders > 0) {
    return `${tt('Tento kanál nesie')} ${formatCount(orders)} ${tt('objednávok za zvolené obdobie, takže ho server zmazať nedovolí. Vypnite ho namiesto toho - vypnutý kanál sa nedá vybrať pre novú objednávku a staré ostanú čitateľné.')}`
  }
  return tt(
    'Za zvolené obdobie nemá objednávky. Server odmietne mazanie, ak nejaké má z inej doby - v takom prípade ho vypnite.',
  )
}

export function deactivateWarning(channel: Channel, trade: readonly ChannelTrade[]): string {
  const orders = ordersFor(channel.slug, trade)
  const carried =
    orders === null || orders === 0
      ? tt('Za zvolené obdobie cezeň nešla ani jedna objednávka.')
      : `${tt('Za zvolené obdobie cezeň prešlo')} ${formatCount(orders)} ${tt('objednávok.')}`
  return `${carried} ${tt('Vypnutý kanál sa nedá vybrať pre novú objednávku; hotové objednávky ostávajú v prehľadoch tak, ako sú.')}`
}

/**
 * Which registers the channel editor offers, ids only, in the order it draws them - §9.650.
 *
 * <p><b>The stored one is always among them.</b> `/fiskal/registers` is a second request, so between the first
 * paint and its answer - or for ever if it fails - a `<select>` whose `value` matches no `<option>` renders
 * **blank** over a configured register, which is §9.562's trap: the screen hides the state it is about. A
 * register the franchise has since deleted is offered for the same reason, and `registerName` prints its id, so
 * an operator meets `terasa` and can act on it rather than an empty box.
 *
 * <p>Extracted rather than inlined because this is the half the owner's report turns on and `ChannelDialog` is
 * not exported - widening a module's surface to reach a component from a test is the trade §3.155 refused.
 */
export function registerChoices(storedKasaId: string, payload: unknown): string[] {
  const offered = registerIds(payload)
  const stored = storedKasaId.trim()
  if (stored === '' || offered.includes(stored)) return offered
  return [stored, ...offered]
}

// ─── The form ────────────────────────────────────────────────────────────────

export interface ChannelForm {
  name: string
  slug: string
  requiresDeliveryAddress: boolean
  usesDrivers: boolean
  usesTables: boolean
  onPremise: boolean
  availableOnWeb: boolean
  allowsDeferredPayment: boolean
  settlesImmediately: boolean
  /** §10A.331, owner O44. */
  offersAddons: boolean
  /** §10A.347, owner #24 (O79). */
  ownBoards: boolean
  /** §10A.374, owner O96. */
  unprintedCloseCancels: boolean
  /** §10A.443, owner O144. */
  offersToCouriers: boolean
  sortOrder: string
  isActive: boolean
  /** The empty string is *no colour*, which is what the backend stores as null. */
  colour: string
  /** §10A.17. The empty string is *use the rule*, which the backend stores as null. */
  kasaId: string
  /** §10A.388, owner O110 - the ticked registers, in the order the register list draws them. */
  printKasaIds: string[]
}

export function formOf(channel: Channel): ChannelForm {
  return {
    name: channel.name,
    slug: channel.slug,
    requiresDeliveryAddress: channel.requiresDeliveryAddress,
    usesDrivers: channel.usesDrivers,
    usesTables: channel.usesTables,
    onPremise: channel.onPremise,
    availableOnWeb: channel.availableOnWeb,
    allowsDeferredPayment: channel.allowsDeferredPayment,
    settlesImmediately: channel.settlesImmediately,
    offersAddons: channel.offersAddons,
    ownBoards: channel.ownBoards,
    unprintedCloseCancels: channel.unprintedCloseCancels,
    offersToCouriers: channel.offersToCouriers,
    sortOrder: String(channel.sortOrder),
    isActive: channel.isActive,
    colour: channel.colour ?? '',
    kasaId: channel.kasaId ?? '',
    printKasaIds: [...channel.printKasaIds],
  }
}

export function emptyForm(existing: readonly Channel[]): ChannelForm {
  const highest = existing.reduce((most, channel) => Math.max(most, channel.sortOrder), 0)
  return {
    name: '',
    slug: '',
    requiresDeliveryAddress: false,
    usesDrivers: false,
    usesTables: false,
    onPremise: true,
    availableOnWeb: false,
    allowsDeferredPayment: false,
    settlesImmediately: false,
    // §10A.331 - off on a new channel, which is the column's own default.
    offersAddons: false,
    // §10A.347 - and so are its own boards, until somebody ticks them.
    ownBoards: false,
    // §10A.374 - **on**, the column's own default and §9.457's rule, so a new counter channel cancels a close
    // without a document until somebody decides it should settle instead.
    unprintedCloseCancels: true,
    // §10A.443 - **on**, the column's own default, so a new driven channel offers to the couriers until somebody
    // keeps it to the restaurant's own drivers.
    offersToCouriers: true,
    sortOrder: String(highest + 10),
    isActive: true,
    colour: '',
    kasaId: '',
    // §10A.388 - no list on a new channel, until somebody ticks one.
    printKasaIds: [],
  }
}

export type ChannelProblem =
  | { kind: 'no-name' }
  | { kind: 'no-slug' }
  | { kind: 'bad-slug' }
  | { kind: 'duplicate-slug'; other: string }
  | { kind: 'sort-not-whole' }
  | { kind: 'address-without-driver' }
  | { kind: 'deferred-without-tables' }

/**
 * What the form refuses, and two combinations it questions.
 *
 * The slug is what `SalesChannel.bySlug` looks a channel up by and what `orders.type` stores, so a duplicate
 * would make two channels indistinguishable to every query in the backend. The two questioned pairs are not
 * refused — a franchise may genuinely want them — but they are the ones that leave an order unclosable.
 */
export function problemsOf(form: ChannelForm, others: readonly Channel[]): ChannelProblem[] {
  const problems: ChannelProblem[] = []
  if (form.name.trim() === '') problems.push({ kind: 'no-name' })
  const slug = form.slug.trim()
  if (slug === '') {
    problems.push({ kind: 'no-slug' })
  } else if (!/^[a-z][a-z0-9_]*$/.test(slug)) {
    problems.push({ kind: 'bad-slug' })
  } else {
    const clash = others.find((channel) => channel.slug === slug)
    if (clash !== undefined) problems.push({ kind: 'duplicate-slug', other: clash.name })
  }
  if (!/^-?\d+$/.test(form.sortOrder.trim())) problems.push({ kind: 'sort-not-whole' })

  // An address-bearing channel with no driver: `OrderStatusService` never asks for one, so the order can go
  // *on the way* with nobody driving it, and the wallet has nobody to attribute the cash to.
  if (form.requiresDeliveryAddress && !form.usesDrivers) {
    problems.push({ kind: 'address-without-driver' })
  }
  // Deferred payment is a tab, and `OrderTabService` opens one against a table.
  if (form.allowsDeferredPayment && !form.usesTables) {
    problems.push({ kind: 'deferred-without-tables' })
  }
  return problems
}

/** The two questioned pairs are warnings; everything else stops the save. */
export function isBlocking(problem: ChannelProblem): boolean {
  return problem.kind !== 'address-without-driver' && problem.kind !== 'deferred-without-tables'
}

export function canSave(form: ChannelForm, others: readonly Channel[]): boolean {
  return problemsOf(form, others).filter(isBlocking).length === 0
}

export function channelPayload(form: ChannelForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    requiresDeliveryAddress: form.requiresDeliveryAddress,
    usesDrivers: form.usesDrivers,
    usesTables: form.usesTables,
    onPremise: form.onPremise,
    availableOnWeb: form.availableOnWeb,
    allowsDeferredPayment: form.allowsDeferredPayment,
    /* §9.657 - **`settlesImmediately` was missing from this payload and had been since `fd10ad94` shipped it.**
       That commit added it to the interface, the parser, the capability list and the form, and not to here - so
       the *Zaplatí sa hneď* checkbox has never saved. The server would have stored it: `FLAGS` names it and
       `update` is `if (body.containsKey(flag))`, so an absent key leaves the column alone. **Nothing was lost by
       accident** - the control simply did nothing, which is FB-979's class on the channel editor's other half,
       found while adding the eighth capability beside it. Measured: `do_ruky` has the column set on the owner's
       data, so the capability is in use and only the screen could not change it. */
    settlesImmediately: form.settlesImmediately,
    offersAddons: form.offersAddons,
    // §10A.347, owner #24 - sent like every flag, so the checkbox saves what it shows.
    ownBoards: form.ownBoards,
    /* §10A.374, owner O96 - **always sent, whether or not the box is drawn.** Off is the whole point of the setting
       (Sereď's counter), so it has to travel; and a channel whose *Zaplatí sa hneď* is unticked keeps whatever it
       holds, which means nothing there and is the right thing to find if the box is ticked again. */
    unprintedCloseCancels: form.unprintedCloseCancels,
    // §10A.443, owner O144 - always sent, whether or not the box is drawn, for `unprintedCloseCancels`' reason.
    offersToCouriers: form.offersToCouriers,
    sortOrder: Number(form.sortOrder.trim()),
    isActive: form.isActive,
    // '' is a person clearing it; the backend stores that as null.
    color: form.colour,
    // §10A.17. Same rule: clearing it means *go back to the capability rule*, not *the dining room*.
    kasaId: form.kasaId === '' ? null : form.kasaId,
    /* §10A.388, owner O110 - **always sent, and empty unless the channel is both on the premises and driven.** The
       box is drawn only there, so a list left on a channel whose capabilities changed would keep narrowing the
       till with nothing on this screen to clear it; sending the empty list is what clears it. */
    printKasaIds: choosesItsRegister(form) ? [...form.printKasaIds] : [],
  }
}

/**
 * Whether the channel offers the register choice - `FRONTEND.md` §9.740, owner O110.
 *
 * **The owner's own condition**: *"na prevádzke aj rozváža šofér je aktívne, tak tam pridáme Ďalšie, že v takomto
 * prípade si môžu zvoliť, ktorá kasa to bude tlačit"* - both ticked. `printRegistersOf` in `@agos/shared` is the
 * same condition on the reading side, so the box and what it does appear and disappear together.
 */
export function choosesItsRegister(form: { onPremise: boolean; usesDrivers: boolean }): boolean {
  return form.onPremise && form.usesDrivers
}

/**
 * The registers drawn as checkboxes: every configured one, then any ticked id the list no longer names.
 *
 * The second half is §9.650's rule for the single *Kasa* select - a stored value is always shown - and it is not
 * defensive: the register list is a second request, and a ticked register the screen could not draw would be one
 * nobody could untick.
 */
export function printRegisterChoices(ticked: readonly string[], payload: unknown): string[] {
  const offered = registerIds(payload)
  return [...offered, ...ticked.filter((id) => !offered.includes(id))]
}

/** Tick or untick one register, keeping the list in the order the checkboxes are drawn. */
export function togglePrintRegister(
  ticked: readonly string[],
  kasaId: string,
  ticks: boolean,
  drawn: readonly string[],
): string[] {
  const next = new Set(ticked)
  if (ticks) next.add(kasaId)
  else next.delete(kasaId)
  return [...drawn.filter((id) => next.has(id)), ...[...next].filter((id) => !drawn.includes(id))]
}

// `isUnreachable` was here and is deleted — §9.35, 2026-08-06. It returned `!channel.isActive`, under
// a docblock promising *"switched off, or on but offered nowhere"* — and the second half is not a
// condition. A channel that is on but not `availableOnWeb` is reachable: the staff pick it, which is
// what `reachNote` below says in so many words. So the name and the comment described a rule the body
// did not have, and the body was one negation any caller could write.
//
// `reachNote` is the thing the screen actually needed and the thing it uses.

export function reachNote(channel: Channel): string {
  if (!channel.isActive) return tt('Vypnutý - nedá sa vybrať pre novú objednávku.')
  if (channel.availableOnWeb) return tt('Vyberá si ho obsluha aj zákazník na webe.')
  return tt('Vyberá si ho len obsluha - na webe sa neponúka.')
}
